/**
 * Catalogue de fenetres parametriques — definitions et constantes.
 *
 * Chaque style definit les valeurs par defaut (largeur, hauteur, allege, vantaux)
 * et le type de rendu 2D/3D a utiliser.
 */

import type { WindowStyle } from '@/lib/types';

// ── Constantes profil alu (en cm) ────────────────────────────

/** Largeur du dormant (cadre fixe) */
export const FRAME_WIDTH = 5;
/** Largeur du profil ouvrant (battant) */
export const SASH_WIDTH = 4;
/** Profondeur totale du cadre (epaisseur dans le mur) */
export const FRAME_DEPTH = 7;
/** Jeu de pose entre la fenetre et le tableau du mur (par cote) */
export const FITTING_GAP = 2;
/** Largeur du montant central (meneau) entre deux vantaux */
export const MULLION_WIDTH = 5;
/** Epaisseur du double vitrage (4mm + 16mm lame + 4mm) */
export const GLAZING_THICKNESS = 2.4;

// ── Appui de fenetre (rejingot) en cm ────────────────────────

/** Saillie de l'appui par rapport a la facade exterieure */
export const SILL_PROJECTION = 4;
/** Epaisseur de la tablette beton */
export const SILL_THICKNESS = 4;
/** Debord lateral de l'appui par rapport a la fenetre (de chaque cote) */
export const SILL_SIDE_OVERHANG = 3;
/** Pente de l'appui en cm (la face avant est plus basse que l'arriere) */
export const SILL_SLOPE = 1;

// ── Porte — constantes en cm ─────────────────────────────────

/** Largeur du cadre huisserie (chambranle) */
export const DOOR_FRAME_WIDTH = 7;
/** Epaisseur du battant de porte */
export const DOOR_LEAF_THICKNESS = 4;
/** Jeu entre battant et huisserie */
export const DOOR_LEAF_GAP = 0.3;
/** Hauteur de la poignee depuis le sol */
export const DOOR_HANDLE_HEIGHT = 100;
/** Epaisseur du seuil porte d'entree */
export const DOOR_THRESHOLD_HEIGHT = 2;
/** Ratio hauteur vitrage sur porte vitree (partie haute) */
export const DOOR_GLAZING_RATIO = 0.55;
/** Largeur des profils acier d'une porte verriere (cadre du vantail et petits bois) */
export const VERRIERE_PROFILE = 3;

// ── Couleurs par defaut ──────────────────────────────────────

/** RAL 7016 — gris anthracite, standard menuiserie alu */
export const DEFAULT_FRAME_COLOR = '#383E42';
/** Couleur appui beton */
export const SILL_COLOR = '#c0b8a8';
/** Vitrage — bleu clair transparent */
export const DEFAULT_GLASS_COLOR = '#a8d8ea';
/** Couleur porte interieure (bois clair) */
export const DEFAULT_DOOR_INTERIOR_COLOR = '#f5f0e8';
/** Couleur porte d'entree (bois fonce) */
export const DEFAULT_DOOR_ENTRY_COLOR = '#5a4a3a';
/** Couleur huisserie */
export const DOOR_FRAME_COLOR = '#e8e0d4';
/** Couleur porte de garage (gris RAL 9006) */
export const DEFAULT_GARAGE_DOOR_COLOR = '#9a9a9a';
/** Couleur porte verriere (acier noir) */
export const DEFAULT_VERRIERE_COLOR = '#1f1f1f';

// ── Definition d'un style du catalogue ───────────────────────

export interface CatalogEntry {
  /** Identifiant du style */
  style: WindowStyle;
  /** Nom affiche dans l'UI */
  label: string;
  /** Largeur par defaut en cm */
  defaultWidth: number;
  /** Hauteur par defaut en cm */
  defaultHeight: number;
  /** Hauteur d'allege par defaut en cm */
  defaultSillHeight: number;
  /** Nombre de vantaux par defaut */
  defaultLeaves: number;
  /** Type d'ouverture pour le rendu 2D */
  openingMode: 'none' | 'swing' | 'tilt' | 'slide';
  /** Applicable aux portes uniquement */
  isDoor: boolean;
  /** Tailles du commerce proposees (largeur totale en cm), facultatives */
  sizes?: { label: string; width: number }[];
}

// ── Catalogue ────────────────────────────────────────────────

export const WINDOW_CATALOG: Record<WindowStyle, CatalogEntry> = {
  'fixed': {
    style: 'fixed',
    label: 'Châssis fixe',
    defaultWidth: 120,
    defaultHeight: 115,
    defaultSillHeight: 100,
    defaultLeaves: 0,
    openingMode: 'none',
    isDoor: false,
  },
  'casement': {
    style: 'casement',
    label: 'Ouvrant à la française',
    defaultWidth: 80,
    defaultHeight: 115,
    defaultSillHeight: 100,
    defaultLeaves: 1,
    openingMode: 'swing',
    isDoor: false,
  },
  'casement-double': {
    style: 'casement-double',
    label: 'Ouvrant double',
    defaultWidth: 140,
    defaultHeight: 115,
    defaultSillHeight: 100,
    defaultLeaves: 2,
    openingMode: 'swing',
    isDoor: false,
  },
  'tilt-turn': {
    style: 'tilt-turn',
    label: 'Oscillo-battant',
    defaultWidth: 80,
    defaultHeight: 115,
    defaultSillHeight: 100,
    defaultLeaves: 1,
    openingMode: 'tilt',
    isDoor: false,
  },
  'sliding-2': {
    style: 'sliding-2',
    label: 'Baie coulissante 2 vantaux',
    defaultWidth: 180,
    defaultHeight: 215,
    defaultSillHeight: 0,
    defaultLeaves: 2,
    openingMode: 'slide',
    isDoor: false,
  },
  'sliding-3': {
    style: 'sliding-3',
    label: 'Baie coulissante 3 vantaux',
    defaultWidth: 270,
    defaultHeight: 215,
    defaultSillHeight: 0,
    defaultLeaves: 3,
    openingMode: 'slide',
    isDoor: false,
  },
  'french-door': {
    style: 'french-door',
    label: 'Porte-fenêtre',
    defaultWidth: 90,
    defaultHeight: 215,
    defaultSillHeight: 0,
    defaultLeaves: 1,
    openingMode: 'swing',
    isDoor: true,
  },
  'french-door-double': {
    style: 'french-door-double',
    label: 'Porte-fenêtre double',
    defaultWidth: 180,
    defaultHeight: 215,
    defaultSillHeight: 0,
    defaultLeaves: 2,
    openingMode: 'swing',
    isDoor: true,
  },
  'door-interior': {
    style: 'door-interior',
    label: 'Porte intérieure',
    defaultWidth: 83,
    defaultHeight: 204,
    defaultSillHeight: 0,
    defaultLeaves: 1,
    openingMode: 'swing',
    isDoor: true,
  },
  'door-interior-double': {
    style: 'door-interior-double',
    label: 'Porte intérieure double',
    defaultWidth: 136,
    defaultHeight: 204,
    defaultSillHeight: 0,
    defaultLeaves: 2,
    openingMode: 'swing',
    isDoor: true,
  },
  'door-interior-glazed': {
    style: 'door-interior-glazed',
    label: 'Porte intérieure vitrée',
    defaultWidth: 83,
    defaultHeight: 204,
    defaultSillHeight: 0,
    defaultLeaves: 1,
    openingMode: 'swing',
    isDoor: true,
  },
  'door-entry': {
    style: 'door-entry',
    label: "Porte d'entrée",
    defaultWidth: 90,
    defaultHeight: 215,
    defaultSillHeight: 0,
    defaultLeaves: 1,
    openingMode: 'swing',
    isDoor: true,
  },
  'door-sliding': {
    style: 'door-sliding',
    label: 'Porte coulissante',
    defaultWidth: 83,
    defaultHeight: 204,
    defaultSillHeight: 0,
    defaultLeaves: 1,
    openingMode: 'slide',
    isDoor: true,
  },
  'door-sliding-verriere': {
    style: 'door-sliding-verriere',
    label: 'Porte coulissante verrière',
    defaultWidth: 166,
    defaultHeight: 205,
    defaultSillHeight: 0,
    defaultLeaves: 2,
    openingMode: 'slide',
    isDoor: true,
    // Deux vantaux a galandage du commerce : 63, 73, 83 ou 93 cm chacun
    sizes: [63, 73, 83, 93].map(l => ({ label: `2 × ${l} cm`, width: 2 * l })),
  },
  'garage-sectional': {
    style: 'garage-sectional',
    label: 'Porte garage sectionnelle',
    defaultWidth: 240,
    defaultHeight: 200,
    defaultSillHeight: 0,
    defaultLeaves: 1,
    openingMode: 'slide',
    isDoor: true,
  },
  'garage-up-and-over': {
    style: 'garage-up-and-over',
    label: 'Porte garage basculante',
    defaultWidth: 240,
    defaultHeight: 200,
    defaultSillHeight: 0,
    defaultLeaves: 1,
    openingMode: 'swing',
    isDoor: true,
  },
  'garage-double': {
    style: 'garage-double',
    label: 'Porte garage double',
    defaultWidth: 500,
    defaultHeight: 200,
    defaultSillHeight: 0,
    defaultLeaves: 2,
    openingMode: 'slide',
    isDoor: true,
  },
};

// ── Helpers ──────────────────────────────────────────────────

/** Retourne l'entree du catalogue pour un style donne */
export function getCatalogEntry(style: WindowStyle): CatalogEntry {
  return WINDOW_CATALOG[style];
}

/** Hauteur effective d'une ouverture (avec fallback sur le catalogue) */
export function getOpeningHeight(opening: { type: string; style?: string; height?: number }): number {
  if (opening.height != null) return opening.height;
  const entry = opening.style ? WINDOW_CATALOG[opening.style as WindowStyle] : undefined;
  if (entry) return entry.defaultHeight;
  return opening.type === 'door' ? 210 : 115;
}

/** Hauteur d'allege effective */
export function getOpeningSillHeight(opening: { type: string; style?: string; sillHeight?: number }): number {
  if (opening.sillHeight != null) return opening.sillHeight;
  const entry = opening.style ? WINDOW_CATALOG[opening.style as WindowStyle] : undefined;
  if (entry) return entry.defaultSillHeight;
  return opening.type === 'door' ? 0 : 100;
}

/** Le plan dessine des arcs d'ouverture : un ouvrant battant qui a au moins un vantail (« Fixe » = 0 vantail) */
export function hasSwingArcs(opening: { style?: string; leaves?: number }): boolean {
  const entry = opening.style ? WINDOW_CATALOG[opening.style as WindowStyle] : undefined;
  return entry?.openingMode === 'swing' && getOpeningLeaves(opening) > 0;
}

/** Nombre de vantaux effectif */
export function getOpeningLeaves(opening: { style?: string; leaves?: number }): number {
  if (opening.leaves != null) return opening.leaves;
  const entry = opening.style ? WINDOW_CATALOG[opening.style as WindowStyle] : undefined;
  return entry?.defaultLeaves ?? 1;
}

/** Couleur du cadre effective */
export function getFrameColor(opening: { frameColor?: string; style?: string }): string {
  if (opening.frameColor) return opening.frameColor;
  if (opening.style?.startsWith('garage')) return DEFAULT_GARAGE_DOOR_COLOR;
  if (opening.style === 'door-sliding-verriere') return DEFAULT_VERRIERE_COLOR;
  if (opening.style === 'door-entry') return DEFAULT_DOOR_ENTRY_COLOR;
  if (opening.style?.startsWith('door-interior')) return DEFAULT_DOOR_INTERIOR_COLOR;
  return DEFAULT_FRAME_COLOR;
}

/** Petits bois d'une porte verriere : une colonne par vantail du commerce, carreaux d'environ 50 cm de haut (vitrage en cm) */
export function verriereGrid(glassW: number, glassH: number): { cols: number; rows: number } {
  return { cols: Math.max(1, Math.round(glassW / 90)), rows: Math.max(1, Math.round(glassH / 50)) };
}

/** Plus grande taille du commerce d'un style qui tient dans `maxWidth` cm (undefined : aucune, ou style sans tailles) */
export function largestSize(style: WindowStyle, maxWidth: number): number | undefined {
  const fits = (WINDOW_CATALOG[style]?.sizes ?? []).map(s => s.width).filter(w => w <= maxWidth);
  return fits.length ? Math.max(...fits) : undefined;
}

/** Largeur totale du trou dans le mur = fenetre + 2 × jeu de pose */
export function getWallOpeningWidth(windowWidth: number): number {
  return windowWidth + 2 * FITTING_GAP;
}

/** Hauteur totale du trou dans le mur = fenetre + jeu haut + jeu bas */
export function getWallOpeningHeight(windowHeight: number): number {
  return windowHeight + 2 * FITTING_GAP;
}

/**
 * Calcule la decomposition interieure d'une fenetre.
 * Retourne les rectangles des vitrages en coordonnees locales (u, v)
 * par rapport au coin bas-gauche du dormant interieur.
 */
export function computeGlazingPanes(
  width: number,
  height: number,
  leaves: number,
  style: WindowStyle
): { x: number; y: number; w: number; h: number }[] {
  // Dimensions interieures du dormant
  const innerW = width - 2 * FRAME_WIDTH;
  const innerH = height - 2 * FRAME_WIDTH;
  if (innerW <= 0 || innerH <= 0) return [];

  if (leaves <= 0) {
    // Chassis fixe — un seul vitrage
    return [{ x: FRAME_WIDTH, y: FRAME_WIDTH, w: innerW, h: innerH }];
  }

  if (style.startsWith('sliding')) {
    // Coulissant — vantaux cote a cote sans meneau (rails superposes)
    const paneW = innerW / leaves;
    return Array.from({ length: leaves }, (_, i) => ({
      x: FRAME_WIDTH + i * paneW,
      y: FRAME_WIDTH,
      w: paneW,
      h: innerH,
    }));
  }

  // Ouvrant / oscillo-battant — vantaux separes par des meneaux
  const totalMullions = leaves - 1;
  const availW = innerW - totalMullions * MULLION_WIDTH;
  const paneW = availW / leaves;

  return Array.from({ length: leaves }, (_, i) => ({
    x: FRAME_WIDTH + i * (paneW + MULLION_WIDTH),
    y: FRAME_WIDTH,
    w: paneW,
    h: innerH,
  }));
}
